import { SearchOutlined } from '@ant-design/icons';
import type { TableProps } from 'antd';
import { Button, Flex, Input, Popover } from 'antd';
import type { CheckboxGroupProps, CheckboxProps } from 'antd/es/checkbox';
import Checkbox from 'antd/es/checkbox';
import { useMemo, useState } from 'react';

import { ColumnVisibilityIcon } from '@/components/Icons';
import type { Option } from '@/utils/types';

import { SEARCH_PLACEHOLDER } from './constants';
import * as Styled from './styles';
import type { ColumnOverrides, GroupColumn, ToggleableColumn } from './types';
import {
  buildOverrides,
  hasSelectionChanged,
  parseColumns,
  resolveCheckedKeys
} from './utils';

const matchesSearch = (label: Option['label'], search: string): boolean =>
  !search ||
  typeof label !== 'string' ||
  label.toLowerCase().includes(search.toLowerCase());

type Props = {
  columns: TableProps['columns'];
  onHide: (value: string[] | undefined) => void;
};

const ColumnVisibilityButton = (props: Props) => {
  const columns = useMemo(
    () =>
      props.columns?.filter((col: ToggleableColumn) => {
        if ((col as GroupColumn).children?.length) return true;
        return col.toggleable !== false;
      }),
    [props.columns]
  );
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');

  // The option list and the applied selection are pure functions of `columns`,
  // so they are derived here instead of mirrored into state: a column that
  // appears or disappears shows up immediately, even with the popover open.
  const { options, value: appliedValue } = useMemo(
    () => parseColumns(columns),
    [columns]
  );
  /**
   * The user's unapplied toggles, held per column key so they outlive
   * everything that is not a decision: prop identity churn from a background
   * re-render, a page change, a refetch reshaping the option list, and closing
   * the popover by clicking outside or pressing Esc. Only Apply and Cancel
   * clear them.
   */
  const [overrides, setOverrides] = useState<ColumnOverrides>({});
  const value = useMemo(
    () => resolveCheckedKeys(options, appliedValue, overrides),
    [options, appliedValue, overrides]
  );
  const hasPendingSelection = hasSelectionChanged(value, appliedValue);

  const filteredOptions = useMemo(
    () => options.filter(option => matchesSearch(option.label, searchTerm)),
    [options, searchTerm]
  );
  const hasHiddenColumnsApplied = columns?.some(col => {
    const group = col as GroupColumn;
    if (group.children?.length) {
      return group.children.some(child => child.hidden);
    }
    return col.hidden;
  });
  // "All" is scoped to whatever is currently visible: with an active search it
  // reflects (and toggles) only the filtered options, leaving the checked state
  // of search-hidden columns untouched.
  const selectedValues = new Set(value);
  const areAllFilteredChecked =
    filteredOptions.length > 0 &&
    filteredOptions.every(option => selectedValues.has(option.value));

  const handleValueChange: CheckboxGroupProps['onChange'] = checked => {
    setOverrides(
      buildOverrides(options, appliedValue, new Set(checked as string[]))
    );
  };

  // Cancel is the explicit discard. Closing any other way keeps the selection,
  // so an outside click, an Esc, or a page change costs the user nothing.
  const handleClear = () => {
    setOverrides({});
    setIsOpen(false);
    setSearchTerm('');
  };

  const handleApply = () => {
    props.onHide(
      options.map(option => option.value).filter(key => !value.includes(key))
    );
    setOverrides({});
    setIsOpen(false);
    setSearchTerm('');
  };

  const handleAllClick: CheckboxProps['onChange'] = e => {
    const filteredKeys = filteredOptions.map(option => option.value);
    const next = e.target.checked
      ? new Set([...value, ...filteredKeys])
      : new Set(value.filter(key => !filteredKeys.includes(key)));

    setOverrides(buildOverrides(options, appliedValue, next));
  };

  // Closing keeps the search for the same reason it keeps the selection: it is
  // where the user left off, not a decision to start over. Apply and Cancel
  // clear it on their own paths.
  const handleOpenChange = (open: boolean) => {
    setIsOpen(open);
  };

  return (
    <Popover
      title='Show / Hide Columns'
      placement='bottomLeft'
      arrow={false}
      trigger='click'
      content={
        <Styled.Container justify='space-between' gap={8} vertical>
          <Input
            allowClear
            prefix={<SearchOutlined />}
            placeholder={SEARCH_PLACEHOLDER}
            value={searchTerm}
            onChange={e => setSearchTerm(e.target.value)}
          />
          <Styled.Content vertical gap={4}>
            <Checkbox checked={areAllFilteredChecked} onChange={handleAllClick}>
              All
            </Checkbox>
            <Styled.CheckboxGroup
              options={filteredOptions}
              value={value}
              onChange={handleValueChange}
            />
          </Styled.Content>
          <Flex gap={8}>
            <Styled.FooterButton onClick={handleClear}>
              Cancel
            </Styled.FooterButton>
            <Styled.FooterButton
              disabled={!hasPendingSelection}
              color='default'
              onClick={handleApply}
              variant='solid'>
              Apply
            </Styled.FooterButton>
          </Flex>
        </Styled.Container>
      }
      open={isOpen}
      onOpenChange={handleOpenChange}>
      <Button
        aria-label='Show / Hide Columns'
        data-testid='column-visibility-button'
        disabled={!columns}
        color='default'
        variant={hasHiddenColumnsApplied ? 'solid' : 'outlined'}
        icon={<ColumnVisibilityIcon />}
      />
    </Popover>
  );
};

export default ColumnVisibilityButton;
